<!-- 铁路合同执行进度跟踪 -->
<template>
  <div class="nx-list-page transport-contract-page">
    <div class="analysis-panel" v-loading="analysisLoading">
      <div class="analysis-head">
        <div>
          <strong>合同客户履约分析</strong>
          <span>铁运入库</span>
        </div>
        <div class="analysis-actions">
          <el-select v-model="analysisQuery.dimension" size="small" class="dimension-select" @change="loadAnalysis">
            <el-option v-for="item in dimensionOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
          <el-button type="primary" plain size="small" icon="el-icon-printer" :loading="pdfExportLoading" @click="handlePdfExport">导出PDF</el-button>
          <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadAnalysis">刷新分析</el-button>
        </div>
      </div>

      <div class="kpi-grid">
        <div v-for="item in summaryCards" :key="item.label" class="kpi-item" :class="item.type">
          <span>{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
          <small v-if="item.unit">{{ item.unit }}</small>
        </div>
      </div>

      <el-tabs v-model="analysisTab" class="analysis-tabs" @tab-click="handleTabClick">
        <el-tab-pane label="履约分析" name="overview">
          <el-row :gutter="10">
            <el-col :lg="14" :md="24">
              <div class="inner-panel">
                <div class="panel-title">合同客户排行</div>
                <el-table :data="customerRank" size="small" stripe height="312" class="analysis-table">
                  <el-table-column prop="custName" label="合同客户" min-width="160" show-overflow-tooltip />
                  <el-table-column label="合同约定量" width="110" align="right">
                    <template slot-scope="{ row }">{{ fmtTon(field(row, ['contractWeight', 'planTotalWeight'])) }}</template>
                  </el-table-column>
                  <el-table-column label="实际到货量" width="110" align="right">
                    <template slot-scope="{ row }">{{ fmtTon(field(row, ['inWeight', 'planExecuteWeight'])) }}</template>
                  </el-table-column>
                  <el-table-column label="完成率" width="86">
                    <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
                  </el-table-column>
                  <el-table-column label="进度偏差率" width="100">
                    <template slot-scope="{ row }">
                      <span :class="riskTextClass(field(row, ['progressDeviation', 'deviationRate']))">{{ signedPct(field(row, ['progressDeviation', 'deviationRate'])) }}</span>
                    </template>
                  </el-table-column>
                  <el-table-column label="预测完货率" width="100">
                    <template slot-scope="{ row }">{{ fmtPct(field(row, ['forecastCompleteRate', 'forecastFinishRate'])) }}</template>
                  </el-table-column>
                  <el-table-column label="风险" width="86">
                    <template slot-scope="{ row }">
                      <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ riskLabel(row.riskLevel, row.riskLevelLabel) }}</el-tag>
                    </template>
                  </el-table-column>
                </el-table>
              </div>
            </el-col>
            <el-col :lg="10" :md="24">
              <div class="inner-panel">
                <div class="panel-title">时间维度趋势</div>
                <div ref="timeTrendChart" class="trend-chart"></div>
              </div>
            </el-col>
          </el-row>
        </el-tab-pane>

        <el-tab-pane label="煤种穿透" name="cargo">
          <div class="inner-panel">
            <div class="panel-title">煤种履约穿透</div>
            <el-table :data="cargoBreakdown" size="small" stripe height="342" class="analysis-table">
              <el-table-column prop="cargoName" label="煤种" min-width="150" show-overflow-tooltip />
              <el-table-column label="合同约定量" width="120" align="right">
                <template slot-scope="{ row }">{{ fmtTon(field(row, ['contractWeight', 'planTotalWeight'])) }}</template>
              </el-table-column>
              <el-table-column label="实际到货量" width="120" align="right">
                <template slot-scope="{ row }">{{ fmtTon(field(row, ['inWeight', 'planExecuteWeight'])) }}</template>
              </el-table-column>
              <el-table-column label="完成率" width="90">
                <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
              </el-table-column>
              <el-table-column label="时间进度" width="90">
                <template slot-scope="{ row }">{{ fmtPct(row.timeProgress) }}</template>
              </el-table-column>
              <el-table-column label="进度偏差率" width="105">
                <template slot-scope="{ row }">
                  <span :class="riskTextClass(field(row, ['progressDeviation', 'deviationRate']))">{{ signedPct(field(row, ['progressDeviation', 'deviationRate'])) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="预测完货率" width="105">
                <template slot-scope="{ row }">{{ fmtPct(field(row, ['forecastCompleteRate', 'forecastFinishRate'])) }}</template>
              </el-table-column>
              <el-table-column label="风险" width="90">
                <template slot-scope="{ row }">
                  <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ riskLabel(row.riskLevel, row.riskLevelLabel) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="urgeSuggestion" label="催货建议" min-width="210" show-overflow-tooltip />
            </el-table>
          </div>
        </el-tab-pane>

        <el-tab-pane label="催货建议" name="urge">
          <div class="inner-panel">
            <div class="panel-title with-actions">
              <span>催货建议清单</span>
              <div>
                <el-select v-model="analysisQuery.riskLevel" clearable size="small" class="risk-select" placeholder="全部风险" @change="loadUrgeSuggestions">
                  <el-option v-for="item in riskOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
                <el-button type="primary" plain size="small" icon="el-icon-download" :loading="urgeExportLoading" @click="handleUrgeExport">导出建议</el-button>
              </div>
            </div>
            <el-table :data="urgeRows" size="small" stripe height="308" class="analysis-table">
              <el-table-column prop="custName" label="合同客户" min-width="150" show-overflow-tooltip />
              <el-table-column prop="cargoName" label="煤种" min-width="120" show-overflow-tooltip />
              <el-table-column label="剩余量" width="100" align="right">
                <template slot-scope="{ row }">{{ fmtTon(field(row, ['remainWeight', 'planRemainWeight'])) }}</template>
              </el-table-column>
              <el-table-column label="完成率" width="86">
                <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
              </el-table-column>
              <el-table-column label="进度偏差率" width="100">
                <template slot-scope="{ row }">
                  <span :class="riskTextClass(field(row, ['progressDeviation', 'deviationRate']))">{{ signedPct(field(row, ['progressDeviation', 'deviationRate'])) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="预测完货率" width="100">
                <template slot-scope="{ row }">{{ fmtPct(field(row, ['forecastCompleteRate', 'forecastFinishRate'])) }}</template>
              </el-table-column>
              <el-table-column label="风险" width="90">
                <template slot-scope="{ row }">
                  <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ riskLabel(row.riskLevel, row.riskLevelLabel) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="riskReasonText" label="风险原因" min-width="170" show-overflow-tooltip />
              <el-table-column prop="urgeSuggestion" label="催货建议" min-width="220" show-overflow-tooltip />
            </el-table>
            <div class="pager-wrap">
              <el-pagination
                background
                layout="total, sizes, prev, pager, next"
                :current-page.sync="analysisQuery.page"
                :page-size.sync="analysisQuery.size"
                :page-sizes="[10, 20, 50, 100]"
                :total="urgeTotal"
                @size-change="loadUrgeSuggestions"
                @current-change="loadUrgeSuggestions"
              />
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="fee-panel">
      <div class="panel-title with-actions">
        <span>翻车机卸煤费履约明细</span>
        <el-tag size="small" type="info" effect="plain">铁运 / 翻车机卸煤费</el-tag>
      </div>
      <el-form :model="feeQuery" inline size="small" class="fee-search-form" @submit.native.prevent>
        <el-form-item label="合同客户">
          <el-input v-model.trim="feeQuery.custName" clearable maxlength="50" placeholder="请输入合同客户" @keyup.enter.native="handleFeeSearch" />
        </el-form-item>
        <el-form-item label="合同编号">
          <el-input v-model.trim="feeQuery.contractNo" clearable maxlength="50" placeholder="请输入合同编号" @keyup.enter.native="handleFeeSearch" />
        </el-form-item>
        <el-form-item label="合同名称">
          <el-input v-model.trim="feeQuery.contractName" clearable maxlength="80" placeholder="请输入合同名称" @keyup.enter.native="handleFeeSearch" />
        </el-form-item>
        <el-form-item label="煤种">
          <el-input v-model.trim="feeQuery.cargoName" clearable maxlength="50" placeholder="请输入煤种" @keyup.enter.native="handleFeeSearch" />
        </el-form-item>
        <el-form-item label="合同周期">
          <el-date-picker
            v-model="feeQuery.contractDateRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            unlink-panels
            class="contract-date-range"
          />
        </el-form-item>
        <el-form-item label="风险等级">
          <el-select v-model="feeQuery.riskLevel" clearable placeholder="全部风险" class="risk-select">
            <el-option v-for="item in riskOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="feeLoading" @click="handleFeeSearch">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="handleFeeReset">重置</el-button>
        </el-form-item>
      </el-form>
      <el-table
        v-loading="feeLoading"
        :data="feeRows"
        size="small"
        stripe
        border
        class="analysis-table fee-table"
        empty-text="暂无翻车机卸煤费履约数据"
      >
        <el-table-column prop="contractNo" label="合同编号" min-width="150" fixed show-overflow-tooltip />
        <el-table-column prop="contractName" label="合同名称" min-width="170" show-overflow-tooltip />
        <el-table-column prop="custName" label="合同客户" min-width="150" show-overflow-tooltip />
        <el-table-column prop="contractSubNo" label="子合同号" min-width="140" show-overflow-tooltip />
        <el-table-column label="费目" width="120">
          <template slot-scope="{ row }">{{ row.feeItemLabel || '翻车机卸煤费' }}</template>
        </el-table-column>
        <el-table-column prop="cargoName" label="煤种" min-width="130" show-overflow-tooltip />
        <el-table-column label="单价" width="95" align="right">
          <template slot-scope="{ row }">{{ fmtMoney(row.unitPrice) }}</template>
        </el-table-column>
        <el-table-column label="税率" width="80" align="right">
          <template slot-scope="{ row }">{{ fmtPct(row.taxRate) }}</template>
        </el-table-column>
        <el-table-column label="不含税金额" width="115" align="right">
          <template slot-scope="{ row }">{{ fmtMoney(row.amountExclTax) }}</template>
        </el-table-column>
        <el-table-column label="价税合计" width="115" align="right">
          <template slot-scope="{ row }">{{ fmtMoney(row.amountInclTax) }}</template>
        </el-table-column>
        <el-table-column label="合同周期" width="190">
          <template slot-scope="{ row }">{{ dateRangeText(row.contractStartTime, row.contractEndTime) }}</template>
        </el-table-column>
        <el-table-column label="合同约定量" width="110" align="right">
          <template slot-scope="{ row }">{{ fmtTon(field(row, ['contractWeight', 'planTotalWeight'])) }}</template>
        </el-table-column>
        <el-table-column label="实际到货量" width="110" align="right">
          <template slot-scope="{ row }">{{ fmtTon(field(row, ['planExecuteWeight', 'inWeight', 'contractStoredInWeight'])) }}</template>
        </el-table-column>
        <el-table-column label="剩余量" width="100" align="right">
          <template slot-scope="{ row }">{{ fmtTon(field(row, ['planRemainWeight', 'remainWeight'])) }}</template>
        </el-table-column>
        <el-table-column label="完成率" width="90">
          <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
        </el-table-column>
        <el-table-column label="时间进度" width="90">
          <template slot-scope="{ row }">{{ fmtPct(row.timeProgress) }}</template>
        </el-table-column>
        <el-table-column label="进度偏差率" width="105">
          <template slot-scope="{ row }">
            <span :class="riskTextClass(field(row, ['progressDeviation', 'deviationRate']))">{{ signedPct(field(row, ['progressDeviation', 'deviationRate'])) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="预测完货率" width="105">
          <template slot-scope="{ row }">{{ fmtPct(field(row, ['forecastCompleteRate', 'forecastFinishRate'])) }}</template>
        </el-table-column>
        <el-table-column label="风险" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ riskLabel(row.riskLevel, row.riskLevelLabel) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="riskReasonText" label="风险原因" min-width="170" show-overflow-tooltip />
        <el-table-column prop="urgeSuggestion" label="催货建议" min-width="220" show-overflow-tooltip />
      </el-table>
      <div class="pager-wrap">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page.sync="feePage.page"
          :page-size.sync="feePage.size"
          :page-sizes="[10, 20, 50, 100]"
          :total="feeTotal"
          @size-change="handleFeeSizeChange"
          @current-change="loadFeePerformance"
        />
      </div>
    </div>

  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  fetchProgressOverview,
  fetchCustomerRank,
  fetchContractCargoBreakdown,
  fetchContractTimeTrend,
  fetchUrgeSuggestionPage,
  exportUrgeSuggestion,
  fetchRailwayFeePerformancePage,
  exportRailwayContractPdf
} from "./api";
import { downloadXls } from '@/util/util'

export default {
  data () {
    return {
      defaultParams: { businessType: 1 },
      analysisLoading: false,
      feeLoading: false,
      pdfExportLoading: false,
      urgeExportLoading: false,
      analysisTab: 'overview',
      overview: {},
      customerRank: [],
      cargoBreakdown: [],
      timeTrend: [],
      urgeRows: [],
      urgeTotal: 0,
      feeRows: [],
      feeTotal: 0,
      timeTrendChart: null,
      feeQuery: {
        custName: '',
        contractNo: '',
        contractName: '',
        cargoName: '',
        contractDateRange: [],
        riskLevel: null
      },
      feePage: {
        page: 1,
        size: 10
      },
      analysisQuery: {
        dimension: 'month',
        riskLevel: null,
        page: 1,
        size: 10
      },
      dimensionOptions: [
        { label: '合同周期', value: 'contractCycle' },
        { label: '年', value: 'year' },
        { label: '月', value: 'month' },
        { label: '周', value: 'week' },
        { label: '日', value: 'day' }
      ],
      riskOptions: [
        { label: '低风险', value: 0 },
        { label: '中风险', value: 1 },
        { label: '高风险', value: 2 }
      ]
    }
  },
  computed: {
    summaryCards () {
      return [
        { label: '合同约定量', value: this.fmtTon(this.field(this.overview, ['contractWeight', 'planTotalWeight'])), unit: '吨' },
        { label: '实际到货量', value: this.fmtTon(this.field(this.overview, ['inWeight', 'planExecuteWeight'])), unit: '吨', type: 'success' },
        { label: '完成率', value: this.fmtPct(this.overview.completeRate) },
        { label: '时间进度', value: this.fmtPct(this.overview.timeProgress) },
        { label: '进度偏差率', value: this.signedPct(this.field(this.overview, ['progressDeviation', 'deviationRate'])), type: this.field(this.overview, ['progressDeviation', 'deviationRate']) < 0 ? 'danger' : 'success' },
        { label: '预测完货率', value: this.fmtPct(this.field(this.overview, ['forecastCompleteRate', 'forecastFinishRate'])) }
      ]
    }
  },
  mounted () {
    window.addEventListener('resize', this.resizeCharts)
    this.loadPageData()
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
  },
  methods: {
    loadPageData () {
      this.loadAnalysis()
      this.loadFeePerformance()
    },
    formatParams (params) {
      const result = Object.assign({}, params)
      Object.keys(result).forEach((key) => {
        if (!result[key] && result[key] !== 0) delete result[key]
      })
      return result
    },
    buildFilterParams () {
      const dateRange = this.feeQuery.contractDateRange || []
      return this.formatParams({
        custName: this.feeQuery.custName,
        contractNo: this.feeQuery.contractNo,
        contractName: this.feeQuery.contractName,
        cargoName: this.feeQuery.cargoName,
        riskLevel: this.feeQuery.riskLevel,
        rangeStart: dateRange[0],
        rangeEnd: dateRange[1]
      })
    },
    buildAnalysisParams (extra = {}) {
      const params = this.buildFilterParams()
      return Object.assign({}, params, {
        businessType: 1,
        feeItem: 3,
        dimension: this.analysisQuery.dimension
      }, extra)
    },
    buildFeeParams (extra = {}) {
      return Object.assign({}, this.buildAnalysisParams({
        page: this.feePage.page,
        size: this.feePage.size
      }), extra)
    },
    async loadAnalysis () {
      this.analysisLoading = true
      try {
        const params = this.buildAnalysisParams({ page: undefined, size: undefined })
        const [overview, customerRank, cargoBreakdown, timeTrend] = await Promise.all([
          fetchProgressOverview(params),
          fetchCustomerRank(params),
          fetchContractCargoBreakdown(params),
          fetchContractTimeTrend(params)
        ])
        this.overview = overview || {}
        this.customerRank = this.toRows(customerRank)
        this.cargoBreakdown = this.toRows(cargoBreakdown)
        this.timeTrend = this.toRows(timeTrend)
        await this.loadUrgeSuggestions(false)
        this.$nextTick(this.renderTimeTrendChart)
      } catch (e) {
        this.$message.error((e && e.message) || '履约分析加载失败')
      } finally {
        this.analysisLoading = false
      }
    },
    async loadUrgeSuggestions (showLoading = true) {
      if (showLoading) this.analysisLoading = true
      try {
        const data = await fetchUrgeSuggestionPage(this.buildAnalysisParams({
          riskLevel: this.analysisQuery.riskLevel,
          page: this.analysisQuery.page,
          size: this.analysisQuery.size
        }))
        const pageData = data || {}
        this.urgeRows = this.toRows(pageData)
        this.urgeTotal = Number(pageData.total || pageData.totalRow || this.urgeRows.length || 0)
      } catch (e) {
        this.$message.error((e && e.message) || '催货建议加载失败')
      } finally {
        if (showLoading) this.analysisLoading = false
      }
    },
    async loadFeePerformance () {
      this.feeLoading = true
      try {
        const data = await fetchRailwayFeePerformancePage(this.buildFeeParams())
        const pageData = data || {}
        this.feeRows = this.toRows(pageData)
        this.feeTotal = Number(pageData.total || pageData.totalRow || this.feeRows.length || 0)
      } catch (e) {
        this.$message.error((e && e.message) || '翻车机卸煤费履约明细加载失败')
      } finally {
        this.feeLoading = false
      }
    },
    handleFeeSearch () {
      this.feePage.page = 1
      this.analysisQuery.page = 1
      this.loadPageData()
    },
    handleFeeReset () {
      this.feeQuery = {
        custName: '',
        contractNo: '',
        contractName: '',
        cargoName: '',
        contractDateRange: [],
        riskLevel: null
      }
      this.handleFeeSearch()
    },
    handleFeeSizeChange (size) {
      this.feePage.size = size
      this.feePage.page = 1
      this.loadFeePerformance()
    },
    handleTabClick () {
      this.$nextTick(this.resizeCharts)
    },
    renderTimeTrendChart () {
      const el = this.$refs.timeTrendChart
      if (!el) return
      this.disposeCharts()
      this.timeTrendChart = echarts.init(el)
      const labels = this.timeTrend.map(item => item.bucketLabel || item.bucketKey || item.monthLabel || item.name)
      const contractWeights = this.timeTrend.map(item => Number(this.field(item, ['contractWeight', 'planTotalWeight'])) || 0)
      const inWeights = this.timeTrend.map(item => Number(this.field(item, ['inWeight', 'planExecuteWeight'])) || 0)
      this.timeTrendChart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: ['合同约定量', '实际到货量'] },
        grid: { left: 14, right: 18, top: 32, bottom: 42, containLabel: true },
        xAxis: { type: 'category', data: labels },
        yAxis: { type: 'value', name: '吨', splitLine: { lineStyle: { type: 'dashed' } } },
        series: [
          { name: '合同约定量', type: 'bar', data: contractWeights, barMaxWidth: 36, itemStyle: { color: '#94a3b8' } },
          { name: '实际到货量', type: 'line', smooth: true, data: inWeights, lineStyle: { width: 3, color: '#1f5fbf' }, itemStyle: { color: '#1f5fbf' } }
        ]
      })
    },
    disposeCharts () {
      if (this.timeTrendChart) {
        this.timeTrendChart.dispose()
        this.timeTrendChart = null
      }
    },
    resizeCharts () {
      if (this.timeTrendChart) this.timeTrendChart.resize()
    },
    toRows (data) {
      if (Array.isArray(data)) return data
      if (!data) return []
      return data.records || data.rows || data.list || data.data || []
    },
    field (row, keys) {
      const source = row || {}
      for (let i = 0; i < keys.length; i += 1) {
        const value = source[keys[i]]
        if (value !== undefined && value !== null && value !== '') return value
      }
      return 0
    },
    fmtTon (v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    fmtPct (v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0%'
      return n.toFixed(2).replace(/\.00$/, '') + '%'
    },
    fmtMoney (v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    dateRangeText (start, end) {
      const left = start ? String(start).slice(0, 10) : '-'
      const right = end ? String(end).slice(0, 10) : '-'
      return `${left} 至 ${right}`
    },
    signedPct (v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0%'
      return (n > 0 ? '+' : '') + n.toFixed(2).replace(/\.00$/, '') + '%'
    },
    riskTextClass (value) {
      return Number(value) < 0 ? 'text-danger' : 'text-success'
    },
    riskTagType (level) {
      const value = Number(level)
      if (value === 2) return 'danger'
      if (value === 1) return 'warning'
      return 'success'
    },
    riskLabel (level, label) {
      if (label) return label
      const value = Number(level)
      if (value === 2) return '高风险'
      if (value === 1) return '中风险'
      return '低风险'
    },
    async handlePdfExport () {
      if ((this.feeTotal || 0) < 1) {
        this.$message.warning('无数据可导出')
        return
      }
      this.pdfExportLoading = true
      try {
        const res = await exportRailwayContractPdf(this.buildAnalysisParams({
          page: undefined,
          size: undefined,
          riskLevel: this.feeQuery.riskLevel
        }))
        const blobData = res.data || res
        const contentType = (res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || ''
        if (contentType && contentType.indexOf('application/pdf') === -1) {
          let errorText = ''
          if (blobData && typeof blobData.text === 'function') {
            errorText = await blobData.text()
          }
          throw new Error(this.parseBlobError(errorText) || 'PDF导出失败')
        }
        if (!blobData || (blobData.size !== undefined && blobData.size < 1)) {
          this.$message.warning('暂无可导出的PDF数据')
          return
        }
        const blob = blobData instanceof Blob ? blobData : new Blob([blobData], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `铁路合同履约分析_${Date.now()}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        this.$message.success('PDF导出成功')
      } catch (e) {
        this.$message.error(e && e.message ? e.message : 'PDF导出失败')
      } finally {
        this.pdfExportLoading = false
      }
    },
    parseBlobError (text) {
      if (!text) return ''
      try {
        const body = JSON.parse(text)
        return body.msg || body.message || (body.data && body.data.msg) || ''
      } catch (e) {
        return text.length > 80 ? text.slice(0, 80) : text
      }
    },
    async handleUrgeExport () {
      if (!this.urgeRows.length) {
        this.$message.warning('暂无催货建议可导出')
        return
      }
      this.urgeExportLoading = true
      try {
        const res = await exportUrgeSuggestion(this.buildAnalysisParams({
          riskLevel: this.analysisQuery.riskLevel,
          page: undefined,
          size: undefined
        }))
        downloadXls(res.data, `铁路合同催货建议_${Date.now()}.xlsx`)
        this.$message.success('导出成功')
      } catch (e) {
        this.$message.error('催货建议导出失败')
      } finally {
        this.urgeExportLoading = false
      }
    }
  }
}
</script>
<style scoped lang="scss">
.transport-contract-page {
  .analysis-panel {
    margin-bottom: 10px;
    padding: 12px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    background: #fff;
    box-sizing: border-box;
  }

  .analysis-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 10px;

    strong {
      margin-right: 10px;
      color: #1f2937;
      font-size: 15px;
    }

    span {
      color: #606266;
      font-size: 12px;
    }
  }

  .analysis-actions,
  .panel-title.with-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .dimension-select {
    width: 118px;
  }

  .risk-select {
    width: 112px;
  }

  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
    margin-bottom: 10px;
  }

  .kpi-item {
    min-height: 82px;
    padding: 12px;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    background: #fafafa;
    box-sizing: border-box;

    span {
      display: block;
      color: #606266;
      font-size: 12px;
    }

    strong {
      display: inline-block;
      margin-top: 9px;
      color: #1f2937;
      font-size: 21px;
      line-height: 1.1;
    }

    small {
      margin-left: 4px;
      color: #909399;
    }

    &.success strong {
      color: #087f5b;
    }

    &.danger strong {
      color: #b42318;
    }
  }

  .analysis-tabs {
    ::v-deep .el-tabs__header {
      margin-bottom: 10px;
    }
  }

  .inner-panel {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
  }

  .fee-panel {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    background: #fff;
    overflow: hidden;
  }

  .fee-search-form {
    padding: 12px 12px 2px;
    border-bottom: 1px solid #ebeef5;

    ::v-deep .el-form-item {
      margin-right: 10px;
      margin-bottom: 10px;
    }

    ::v-deep .el-input,
    ::v-deep .el-select {
      width: 170px;
    }
  }

  .contract-date-range {
    width: 240px;
  }

  .fee-table {
    ::v-deep .el-table__fixed-right::before,
    ::v-deep .el-table__fixed::before {
      background: #ebeef5;
    }
  }

  .panel-title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 40px;
    padding: 0 12px;
    border-bottom: 1px solid #ebeef5;
    color: #1f2937;
    font-weight: 600;
    box-sizing: border-box;
  }

  .analysis-table {
    width: 100%;

    ::v-deep th.el-table__cell {
      background: #f5f7fa;
      color: #1f2937;
    }
  }

  .trend-chart {
    width: 100%;
    height: 312px;
  }

  .pager-wrap {
    display: flex;
    justify-content: flex-end;
    padding: 10px 12px;
  }

  .text-success {
    color: #087f5b;
  }

  .text-danger {
    color: #b42318;
  }
}

@media (max-width: 1200px) {
  .transport-contract-page {
    .kpi-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }
}

@media (max-width: 768px) {
  .transport-contract-page {
    .analysis-head,
    .analysis-actions {
      align-items: flex-start;
      flex-direction: column;
    }

    .kpi-grid {
      grid-template-columns: 1fr;
    }
  }
}
</style>
